// "I changed my mind — this drum is not finished after all."
//
// Adding a phase behind a drum the warehouse is about to take (or has taken)
// puts the whole thing in reverse: the job reopens in Projects, the drum leaves
// the Production → Warehouse queue and reappears in Project Bobin State, and any
// StorageItem the warehouse had already made for it is released.
//
// All of that is one endpoint. This is the dialog in front of it, shared by the
// two places a finished drum is looked at — the Production → Warehouse table and
// the yard in Factory 3D — because "the same thing as the table does" is the
// requirement, and two copies of it would not stay the same for long.
import { useState } from 'react';
import axios from 'axios';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { API } from '../../config/api';

// Adding a phase is a PRODUCTION edit, so it goes to the jobs service — the
// warehouse only gets told to let go of the drum, by that service.
const prod = axios.create({ baseURL: API.REDIS, timeout: 15000 });
prod.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers['x-auth-token'] = token;
  return config;
});

// Phases a finished drum can still be sent to. Coiling and rewinding are the
// everyday ones; an outer sheath added this late is rare but real (a lot sold
// bare that a customer then wants jacketed).
export const ADDABLE_PHASES = [
  { value: 'coiling', label: 'Coiling', hint: 'Cut the drum into coils' },
  { value: 'rewinding', label: 'Rewinding', hint: 'Drum → drum transfer' },
  {
    value: 'exterior_sheat',
    label: 'Exterior sheath',
    hint: 'Rare — jacket a cable that was finished bare'
  }
];

/**
 * One drum per request, grouped by job.
 *
 * `add-phase` is per JOB: it takes the job in the URL and the drums of that job
 * in `tokenIds`. So a selection spanning several products is several requests,
 * and the tokens have to be grouped by their own job or one job's drums would be
 * sent to another job's recipe.
 */
function groupByJob(targets = []) {
  const byJob = new Map();
  for (const t of targets) {
    const jobId = t?.jobId ? String(t.jobId) : null;
    if (!jobId) continue;
    const entry = byJob.get(jobId) || { jobId, tokenIds: [], phases: new Set(), labels: new Set() };
    if (t.tokenId) entry.tokenIds.push(String(t.tokenId));
    if (t.phase) entry.phases.add(String(t.phase));
    if (t.cableName || t.productLabel) entry.labels.add(String(t.cableName || t.productLabel));
    byJob.set(jobId, entry);
  }
  return [...byJob.values()];
}

/**
 * `target` is whatever the caller knows about the drum. Only `jobId` is
 * required; `tokenId` narrows the change to this drum instead of every drum at
 * the same phase, and `phase` says which phase the new one follows.
 *
 * `targets` is the same thing for SEVERAL drums at once — the Project Token State
 * selection, which can span products. Either one opens the dialog.
 *
 * `onDone({ ok, variant, message, data })` reports back so the caller can flash
 * it in its own style and refetch its own lists.
 */
export default function AddPhaseDialog({ target, targets, onHide, onDone }) {
  const [phaseName, setPhaseName] = useState('coiling');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // One shape inside, whichever way it was opened.
  const list = Array.isArray(targets) && targets.length ? targets : target ? [target] : [];
  const show = list.length > 0;
  const jobs = groupByJob(list);
  const many = list.length > 1;
  const first = list[0] || {};

  const submit = async () => {
    if (!jobs.length) {
      setError(
        many
          ? 'None of the selected drums has a job attached — cannot add a phase.'
          : 'This drum has no job attached — cannot add a phase to it.'
      );
      return;
    }

    setBusy(true);
    setError('');
    try {
      const label =
        ADDABLE_PHASES.find(p => p.value === phaseName)?.label || phaseName;

      // Sequential, not in parallel: each one reopens a job, moves drums between
      // two services and may release a StorageItem. A failure half way through
      // has to be reportable as "these went, those did not", which a Promise.all
      // rejection cannot tell you.
      const okJobs = [];
      const failedJobs = [];
      const kept = [];
      let lastData = null;

      for (const job of jobs) {
        try {
          const { data } = await prod.post(`/api/redis/jobs/${job.jobId}/add-phase`, {
            name: phaseName,
            ...(job.tokenIds.length ? { tokenIds: job.tokenIds } : {}),
            // Only when every drum of this job sits at the same phase. Mixed,
            // the server works it out from the tokens — passing one phase for
            // drums at two would hang the new phase off the wrong one.
            ...(job.phases.size === 1 ? { dependsOn: [...job.phases][0] } : {})
          });
          lastData = data;
          okJobs.push(job);
          for (const f of data?.releaseErrors || []) kept.push(f.error);
        } catch (e) {
          failedJobs.push({
            job,
            error: e.response?.data?.error || e.message
          });
        }
      }

      // Nothing landed: keep the dialog open with the reason, rather than
      // closing on a success message that is not true.
      if (!okJobs.length) {
        setError(failedJobs.map(f => f.error).join('; ') || 'Nothing was added.');
        return;
      }

      const drums = okJobs.reduce((n, j) => n + (j.tokenIds.length || 1), 0);
      const what = many
        ? `${drums} drum${drums === 1 ? '' : 's'} across ${okJobs.length} product${
            okJobs.length === 1 ? '' : 's'
          }`
        : first.cableName || first.productLabel || 'the drum';

      const problems = [
        ...kept.map(e => `the warehouse kept a drum: ${e}`),
        ...failedJobs.map(f => `${[...f.job.labels][0] || f.job.jobId}: ${f.error}`)
      ];

      onDone?.({
        ok: true,
        variant: problems.length ? 'warning' : 'success',
        message: problems.length
          ? `${label} added to ${what}, with ${problems.length} problem(s): ${problems.join('; ')}`
          : `${label} added — ${what} back in production. Nothing is scheduled yet; ` +
            'plan it from Project Token State.',
        data: lastData,
        jobIds: okJobs.map(j => j.jobId)
      });
      onHide?.();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (busy) return;
    setError('');
    onHide?.();
  };

  return (
    <Modal show={show} onHide={close} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-7">
          {many
            ? `Add phase — ${list.length} drums`
            : `Add phase — ${first.cableName || first.productLabel || 'drum'}`}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {many ? (
          // Which products and how many drums of each: the selection is made in
          // a table that may be filtered and paged, so "27 selected" is not
          // something anybody can check by looking at it.
          <div className="fs-9 mb-3">
            <div className="text-body-tertiary mb-1">
              {list.length} drums across {jobs.length} product
              {jobs.length === 1 ? '' : 's'}
              {jobs.length > 1 ? ' — one request per product' : ''}:
            </div>
            {jobs.map(j => (
              <div key={j.jobId} className="d-flex justify-content-between">
                <span>{[...j.labels][0] || j.jobId}</span>
                <span className="text-body-tertiary">
                  {j.tokenIds.length} drum{j.tokenIds.length === 1 ? '' : 's'}
                  {j.phases.size === 1
                    ? ` · at ${[...j.phases][0].replaceAll('_', ' ')}`
                    : j.phases.size > 1
                      ? ` · at ${j.phases.size} different phases`
                      : ''}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-body-tertiary fs-9">
            {first.displayId}
            {first.lengthMeters
              ? ` · ${Number(first.lengthMeters).toLocaleString()} m`
              : ''}
            {first.phase
              ? ` · finished at ${String(first.phase).replaceAll('_', ' ')}`
              : ''}
          </p>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Phase to add</Form.Label>
          <Form.Select
            value={phaseName}
            autoFocus
            disabled={busy}
            onChange={e => setPhaseName(e.target.value)}
          >
            {ADDABLE_PHASES.map(p => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </Form.Select>
          <Form.Text className="text-body-tertiary">
            {ADDABLE_PHASES.find(p => p.value === phaseName)?.hint}
          </Form.Text>
        </Form.Group>

        {error && (
          <Alert variant="danger" className="py-2 fs-9">
            {error}
          </Alert>
        )}

        <Alert variant="info" className="py-2 mb-0 fs-9">
          The drum goes back to production: the job reopens in{' '}
          <strong>Projects</strong>, the drum leaves the warehouse queue and
          reappears in <strong>Project Token State</strong>. Nothing is
          scheduled — pick it there and run the next-phase wizard to choose a
          machine and a length.
          {list.some(t => t?.locationKind === 'slot') && (
            <span className="d-block text-warning mt-1">
              {many
                ? 'Some of these are already on a rack — take them off in the warehouse first, or those releases will be refused.'
                : 'This drum is already on a rack — take it off in the warehouse first, or the release will be refused.'}
            </span>
          )}
        </Alert>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={close} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" onClick={submit} disabled={busy}>
          {busy ? 'Adding…' : 'Add phase'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
